import { createTrailDataVersion, type TrailDataVersion } from '../domain/trails.ts';

type ReportingProvider = 'api' | 'none' | 'tally';

export interface PublicRuntimeConfig {
  readonly schemaVersion: 1;
  readonly trailDataVersion: TrailDataVersion;
  readonly reportingEnabled: boolean;
  readonly reportingProvider: ReportingProvider;
  readonly tallyFormUrl: string | null;
  readonly apiBaseUrl: string | null;
}

export interface RuntimeConfigResolution {
  readonly config: PublicRuntimeConfig;
  readonly source: 'fallback' | 'runtime';
}

const expectedKeys = new Set([
  'apiBaseUrl',
  'reportingEnabled',
  'reportingProvider',
  'schemaVersion',
  'tallyFormUrl',
  'trailDataVersion',
]);
const reportingProviders = new Set<ReportingProvider>(['api', 'none', 'tally']);

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parsePublicHttpsUrl(value: unknown, field: string): string | null {
  if (value === null) return null;
  if (typeof value !== 'string') throw new TypeError(`${field} must be null or a URL`);
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new TypeError(`${field} must be null or a URL`);
  }
  if (
    parsed.protocol !== 'https:' ||
    parsed.username !== '' ||
    parsed.password !== '' ||
    parsed.search !== '' ||
    parsed.hash !== ''
  ) {
    throw new TypeError(`${field} must be a public HTTPS URL without credentials or parameters`);
  }
  return parsed.href;
}

function parseTallyFormUrl(value: unknown): string | null {
  const parsed = parsePublicHttpsUrl(value, 'tallyFormUrl');
  if (parsed === null) return null;
  const hostname = new URL(parsed).hostname;
  if (hostname !== 'tally.so' && hostname !== 'forms.tally.so') {
    throw new TypeError('tallyFormUrl must use the public Tally host');
  }
  return parsed;
}

function parseApiBaseUrl(value: unknown): string | null {
  if (value === null) return null;
  if (value === '/api' || value === '/api/') return value;
  return parsePublicHttpsUrl(value, 'apiBaseUrl');
}

export function safeRuntimeConfig(trailDataVersion: TrailDataVersion): PublicRuntimeConfig {
  return Object.freeze({
    schemaVersion: 1,
    trailDataVersion,
    reportingEnabled: false,
    reportingProvider: 'none',
    tallyFormUrl: null,
    apiBaseUrl: null,
  });
}

export function validatePublicRuntimeConfig(
  value: unknown,
  expectedTrailDataVersion: TrailDataVersion,
): PublicRuntimeConfig {
  if (!isRecord(value)) throw new TypeError('runtime configuration must be an object');
  const unexpectedKeys = Object.keys(value).filter((key) => !expectedKeys.has(key));
  if (unexpectedKeys.length > 0) {
    throw new TypeError(
      `runtime configuration contains unknown fields: ${unexpectedKeys.sort().join(', ')}`,
    );
  }
  if (value['schemaVersion'] !== 1) {
    throw new TypeError('runtime configuration schema version is unsupported');
  }
  const trailDataVersion = createTrailDataVersion(value['trailDataVersion']);
  if (trailDataVersion !== expectedTrailDataVersion) {
    throw new TypeError('runtime configuration trail version does not match the manifest');
  }
  if (typeof value['reportingEnabled'] !== 'boolean') {
    throw new TypeError('reportingEnabled must be boolean');
  }
  if (
    typeof value['reportingProvider'] !== 'string' ||
    !reportingProviders.has(value['reportingProvider'] as ReportingProvider)
  ) {
    throw new TypeError('reportingProvider is unsupported');
  }
  const reportingProvider = value['reportingProvider'] as ReportingProvider;
  const tallyFormUrl = parseTallyFormUrl(value['tallyFormUrl']);
  const apiBaseUrl = parseApiBaseUrl(value['apiBaseUrl']);
  if (reportingProvider === 'none' && value['reportingEnabled']) {
    throw new TypeError('reporting cannot be enabled without a provider');
  }
  if (reportingProvider === 'none' && (tallyFormUrl !== null || apiBaseUrl !== null)) {
    throw new TypeError('the disabled provider cannot expose reporting endpoints');
  }
  if (reportingProvider === 'tally' && value['reportingEnabled'] && tallyFormUrl === null) {
    throw new TypeError('the Tally provider requires tallyFormUrl');
  }
  if (reportingProvider === 'tally' && apiBaseUrl !== null) {
    throw new TypeError('the Tally provider cannot expose apiBaseUrl');
  }
  if (reportingProvider === 'api' && value['reportingEnabled'] && apiBaseUrl === null) {
    throw new TypeError('the API provider requires apiBaseUrl');
  }
  if (reportingProvider === 'api' && tallyFormUrl !== null) {
    throw new TypeError('the API provider cannot expose tallyFormUrl');
  }

  return Object.freeze({
    schemaVersion: 1,
    trailDataVersion,
    reportingEnabled: value['reportingEnabled'],
    reportingProvider,
    tallyFormUrl,
    apiBaseUrl,
  });
}

export function resolvePublicRuntimeConfig(
  value: unknown,
  expectedTrailDataVersion: TrailDataVersion,
): RuntimeConfigResolution {
  try {
    return Object.freeze({
      config: validatePublicRuntimeConfig(value, expectedTrailDataVersion),
      source: 'runtime',
    });
  } catch {
    return Object.freeze({
      config: safeRuntimeConfig(expectedTrailDataVersion),
      source: 'fallback',
    });
  }
}
